import {Document, isScalar, parseDocument, visit} from "yaml";
import {describeYamlError} from "./parseDlvTests";

let nextKey = 1;

/**
 * A test as the form edits it. "key" and "open" only exist in the form: they are never written in the YAML.
 */
export function newFormCase(values = {}) {
    return {key: nextKey++, open: true, description: '', facts: '', expected: [''], ...values};
}

// What stops a case from running, or null when it is complete
export function caseProblem(testCase) {
    if (testCase.facts.trim() === '') {
        return 'Mancano i fatti';
    }
    if (testCase.expected.length === 0 || testCase.expected.some(atom => atom.trim() === '')) {
        return 'Manca un atomo atteso';
    }
    return null;
}

function textField(testCase, field, number, {nullable}) {
    const value = testCase[field];
    if (typeof value === 'string') {
        return {value};
    }
    if (value == null && nullable) {
        return {value: ''};
    }
    return {error: `Il caso ${number} ha un valore di "${field}" che il modulo non sa mostrare: correggilo nella modalità YAML.`};
}

/**
 * Reads the YAML of the editor into the tests the form shows.
 *
 * @return {{cases: ?Array, error: ?string}} the YAML is empty when there are no cases,
 * the error says what the form can't show (and should be fixed in the YAML)
 */
export function readFormCases(text) {
    if (text.trim() === '') {
        return {cases: [], error: null};
    }

    const document = parseDocument(text);
    if (document.errors.length > 0) {
        return {cases: null, error: describeYamlError(document.errors[0])};
    }

    const data = document.toJS();
    if (data == null) {
        return {cases: [], error: null};
    }
    const items = Array.isArray(data) ? data : data?.cases;
    if (!Array.isArray(items)) {
        return {cases: null, error: 'Il YAML deve contenere l\'elenco "cases".'};
    }

    const cases = [];
    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const number = i + 1;
        if (item === null || typeof item !== 'object' || Array.isArray(item)) {
            return {cases: null, error: `Il caso ${number} non è valido: correggilo nella modalità YAML.`};
        }

        const description = textField(item, 'description', number, {nullable: true});
        const facts = textField(item, 'facts', number, {nullable: true});
        const expected = item.expected == null ? [] : item.expected;
        const error = description.error ?? facts.error
            ?? (!Array.isArray(expected) || expected.some(atom => atom != null && typeof atom !== 'string')
                ? `Il caso ${number} ha atomi attesi che il modulo non sa mostrare: correggili nella modalità YAML.`
                : null);
        if (error) {
            return {cases: null, error};
        }

        cases.push(newFormCase({
            description: description.value,
            facts: facts.value,
            // An empty list item ("- ") is null in the YAML
            expected: expected.length > 0 ? expected.map(atom => atom ?? '') : [''],
            open: items.length <= 3
        }));
    }
    return {cases, error: null};
}

/**
 * Puts the tests of the second YAML after the ones of the first.
 *
 * @return {{text: ?string, error: ?string}} the error says which of the two the form can't read
 */
export function mergeTests(currentText, addedText) {
    const current = readFormCases(currentText);
    if (current.error) {
        return {text: null, error: `I test dell'editor non si possono unire. ${current.error}`};
    }
    const added = readFormCases(addedText);
    if (added.error) {
        return {text: null, error: `I test da importare non si possono unire. ${added.error}`};
    }
    return {text: writeFormCases([...current.cases, ...added.cases]), error: null};
}

/**
 * Writes the tests of the form as the YAML shown in the editor. Facts are always a block, as in the example.
 */
export function writeFormCases(cases) {
    if (cases.length === 0) {
        return '';
    }

    const document = new Document({
        cases: cases.map(({description, facts, expected}) => ({
            ...(description !== '' && {description}),
            facts,
            expected
        }))
    });
    visit(document, {
        Pair(key, pair) {
            if (pair.key?.value === 'facts' && isScalar(pair.value) && pair.value.value !== '') {
                pair.value.type = 'BLOCK_LITERAL';
            }
        }
    });
    return document.toString({lineWidth: 0});
}
